import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import { font, Theme } from '../../utils/themes';
import { useToast } from '../toast/ToastContext';
import StyledText from './StyledText';
import ImageAvtar from '../../assets/images/img-avtar.png';
import { hexToRgba } from '../../utils/helper';
import DeleteIcon from '../../assets/icons/DeleteIcon';

interface UploadedImage {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}

interface StandaloneFileUploadProps {
    multiple?: boolean;
    accept?: string;
    onChange?: (image: UploadedImage[]) => void;
    showRenameInput?: boolean;
    icon?: React.ReactNode;
    removeFile?: () => void;
    isResponsive?: boolean;
    // 👇 NEW PROPS
    previewUrl?: string;        // for existing logo
    uploadProgress?: number;    // 0 to 100
    isUploading?: boolean;
    existingFileName?: string;
    existingFileSize?: number;
}

// ✅ Define isValidFile at top level - flexible based on accept prop
const getValidationConfig = (accept: string) => {
    if (accept.includes('video')) {
        return {
            types: ["video/mp4", "video/quicktime", "video/x-matroska", "video/mp4"],
            extensions: [".mp4", ".mov", ".mkv", ".m4v"],
            displayText: "MP4, MOV, MKV & M4V"
        };
    } else {
        return {
            types: ["image/png", "image/jpeg", "image/webp"],
            extensions: [".png", ".jpg", ".jpeg", ".webp"],
            displayText: "PNG, JPG, JPEG & Webp"
        };
    }
};

const isValidFile = (file: File, accept: string): boolean => {
    const config = getValidationConfig(accept);
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    return config.types.includes(file.type) || config.extensions.includes(ext);
};

const StyledImageUpload: React.FC<StandaloneFileUploadProps> = ({
    multiple = false,
    accept = "*/*",
    onChange,
    showRenameInput = false,
    icon,
    removeFile,
    isResponsive,
    previewUrl,
    uploadProgress = 0,
    isUploading = false,
    existingFileName,
    existingFileSize
}) => {
    const { theme } = useTheme();
    const [files, setFiles] = useState<UploadedImage[]>([]);
    const [localPreview, setLocalPreview] = useState<string | null>(null);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const showToast = useToast();

    const displayPreview = previewUrl || localPreview;
    const displayName = files[0]?.name || existingFileName;
    const displaySize = files[0]?.file?.size || existingFileSize;

    // ✅ Handle file input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const selectedFiles = Array.from(e.target.files).filter(file => isValidFile(file, accept));

        if (selectedFiles.length === 0) {
            const config = getValidationConfig(accept);
            showToast("Upload Failed", `Only ${config.displayText} allowed.`, "error");
            return;
        }

        if (selectedFiles[0]) {
            const reader = new FileReader();
            reader.onload = () => {
                setLocalPreview(reader.result as string);
            };
            reader.readAsDataURL(selectedFiles[0]);
        }

        const uploadedImage: UploadedImage[] = selectedFiles.map((file) => {
            const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
            return {
                file,
                name: file.name,
                type: file.type,
                rename: nameWithoutExt,
                progress: 0,
            };
        });

        const updatedFiles = multiple ? [...files, ...uploadedImage] : uploadedImage.slice(0, 1);
        setFiles(updatedFiles);
        onChange?.(updatedFiles);
    };

    // ✅ Drag & Drop Handlers
    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragging(false);
        const droppedFiles = Array.from(event.dataTransfer.files).filter(file => isValidFile(file, accept));
        if (droppedFiles.length === 0) {
            const config = getValidationConfig(accept);
            showToast("Upload Failed", `Only ${config.displayText} allowed.`, "error");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setLocalPreview(reader.result as string);
        };
        reader.readAsDataURL(droppedFiles[0]);

        const uploadedImage: UploadedImage[] = droppedFiles.map((file) => {
            const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
            return {
                file,
                name: file.name,
                type: file.type,
                rename: nameWithoutExt,
                progress: 0,
            };
        });

        const updatedFiles = multiple ? [...files, ...uploadedImage] : uploadedImage.slice(0, 1);
        setFiles(updatedFiles);
        onChange?.(updatedFiles);
    }, [multiple, files, onChange, showToast, accept]);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!dragging) setDragging(true);
    };

    const handleDragLeave = () => {
        setDragging(false);
    };


    return (
        <>
            {displayPreview ? (
                <DropZone theme={theme} style={{
                    backgroundColor: '#0556CB1A',
                    borderColor: '#0556CB',

                }}>
                    <div style={{ display: 'flex' }}>
                        {files[0]?.type?.startsWith('video/') || accept.includes('video') ? (
                            <video
                                src={displayPreview}
                                style={{
                                    width: '80px',
                                    height: '70px',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    backgroundColor: theme.CorporateTheme.colors.background
                                }}
                                controls={false}
                            />
                        ) :

                            <img
                                src={displayPreview}
                                alt="Preview"
                                style={{
                                    width: '80px',
                                    height: '70px',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    backgroundColor: theme.CorporateTheme.colors.background
                                }}
                            />}
                        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '16px', width: '100%' }}>

                            <div className='d-flex align-items-center justify-content-between '>
                                <StyledText fontSize='16px' fontWeight={600} style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '280px' // Adjust based on your needs
                                }}>{displayName || 'Uploading...'}</StyledText>
                                {!isUploading && removeFile && (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = "";
                                            }
                                            setLocalPreview(null);
                                            setFiles([]);
                                            removeFile?.();
                                        }}
                                        title="Remove logo"
                                    >
                                        <DeleteIcon color={theme.CorporateTheme.colors.redOrange} />
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: 5 }}>
                                <StyledText fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} fontFamily={font.lato}>
                                    {displaySize ? `${Math.round(displaySize / 1024)}KB` : '...'}
                                </StyledText>
                                {isUploading && <>
                                    <StyledText textColor={hexToRgba(theme.CorporateTheme.colors.textSecondary, 0.3)} fontSize='14px'>•</StyledText>
                                    <StyledText fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} fontFamily={font.lato}>
                                        {Math.max(1, Math.round((100 - uploadProgress) * 0.1))} seconds left
                                    </StyledText>
                                </>}
                            </div>


                            {isUploading && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginTop: 10
                                }}>
                                    <div style={{
                                        flex: 1,
                                        height: '6px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '3px',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        <div style={{
                                            height: '100%',
                                            width: `${uploadProgress}%`,
                                            backgroundColor: theme.CorporateTheme.colors.primary,
                                            transition: 'width 0.3s',
                                        }} />
                                    </div>

                                    <div style={{
                                        fontSize: '12px',
                                        color: '#666',
                                        minWidth: '30px',
                                        textAlign: 'right'
                                    }}>
                                        {Math.round(uploadProgress)}%
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>

                </DropZone>
            ) :





                <DropZone
                    theme={theme}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    tabIndex={0}
                    role="button"
                    aria-label="File Upload Dropzone"
                    dragging={dragging} // ✅ Now defined
                    style={{
                        backgroundColor: '#0556CB1A',
                        borderColor: '#0556CB'
                    }}
                >


                    {/* Default Prompt */}
                    {!displayPreview && !isUploading && (
                        <div className='d-flex align-items-center justify-content-center gap-4'>
                            <div className='mr-4'>
                                <img src={ImageAvtar} width={'58px'} height={'58px'} alt='Upload image' />
                            </div>
                            <div>
                                <StyledText
                                    textColor={theme.CorporateTheme.colors.primary}
                                    fontSize={isResponsive ? "14px" : "16px"}
                                    fontWeight={600}
                                    className="mt-2"
                                >
                                    Drop your {accept.includes('video') ? 'video' : 'image'} here, or browse
                                </StyledText>
                                <StyledText
                                    textColor={theme.CorporateTheme.colors.textSecondary}
                                    fontSize={isResponsive ? "12px" : "14px"}
                                    fontWeight={500}
                                    className="mt-2"
                                    lineHeight="20px"
                                >
                                    Supports: {getValidationConfig(accept).displayText}
                                </StyledText>
                            </div>
                        </div>
                    )}

                    <HiddenInput
                        ref={fileInputRef}
                        type="file"
                        multiple={multiple}
                        accept={accept}
                        onChange={handleInputChange}
                    />
                </DropZone>}
        </>
    );
};

export default StyledImageUpload;

// ✅ Styled Components (keep your existing ones)
const DropZone = styled.div<{ theme: Theme; dragging?: boolean }>`
  border: ${({ theme, dragging }) =>
        dragging ? `2px dashed ${theme.CorporateTheme.colors.primary}` : `1px dashed ${theme.CreateFormTheme.colors.inputBorders}`};
  border-radius: 6px;
  padding: 30px;
  color: #666;
  cursor: pointer;
  position: relative;
  user-select: none;
  &:focus {
    outline: 2px solid #007bff;
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;